import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import type { ActionButton } from '@app/src/components/ActionButtons'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewExport from '@app/src/components/Drawer/Views/DrawerViewExport'
import DrawerViewRiskAssessment from '@app/src/components/Drawer/Views/DrawerViewRiskAssessment'
import { useDrawer_DEPRECATED } from '@app/src/components/Drawer_DEPRECATED'
import EmptyState from '@app/src/components/EmptyState'
import UpdateProvidersDrawer from '@app/src/components/ManageProviderDrawer/UpdateProvidersDrawer'
import Table from '@app/src/components/Table'
import { AllAssessmentsExportColumnsAccessor } from '@app/src/export-columns/allAssessments'
import usePagination from '@app/src/hooks/pagination'
import { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import useSort from '@app/src/hooks/sorting'
import { useBulkCreateExpectation } from '@app/src/hooks/useBulkCreateExpectation'
import { useSelectableRows } from '@app/src/hooks/useSelectableRows/useSelectableRows'
import { useWriteAccess } from '@app/src/hooks/useWriteAccess'
import { CONFIGURATION_PAGE_IDS } from '@app/src/pages/Configurations/ConfigurationsScene'
import type { ResourceCollectionSceneProps } from '@app/src/pages/ResourceCollection/ResourceCollectionScene'
import ResourceCollectionScene from '@app/src/pages/ResourceCollection/index'
import type Assessment from '@app/src/types/assessment'
import type { Provider } from '@app/src/types/organizations'
import type { AssessmentTemplate } from '@app/src/types/resourceExplorer'
import { insertIf } from '@app/src/utils/helpersTs'
import { br } from '@app/src/utils/translationMarkup'
import { AmplitudeTrackingPages, ResourceTypes } from '@app/src/wf-constants'
import paths from '@app/src/wf-constants/paths'
import { PublishedWithChangesOutlined } from '@mui/icons-material'
import ChatIcon from '@mui/icons-material/Chat'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { generatePath } from 'react-router'
import AssessmentFilters from '../../ResourceCollection/Filters/AllAssessmentsFilters'
import { Operators } from '../../ResourceCollection/Filters/useFilters'
import { useOrganizedAssessmentData } from '../useOrganizedAssessmentData'
import AllAssessmentsCompareHeader from './AllAssessmentsCompareHeader'
import AllAssessmentsCompareRow from './AllAssessmentsCompareRow'
import { AllAssessmentsViews } from './AllAssessmentsScene'

type AssessmentCompareSceneProps = {
  allowedFilters: string[]
  activatedAssessmentTemplates: AssessmentTemplate[]
  assessedPeriodNames: string[]
} & Pick<ResourceCollectionSceneProps, 'actionButtons' | 'tabs'>

const AssessmentCompareScene: React.FC<AssessmentCompareSceneProps> = ({
  allowedFilters,
  activatedAssessmentTemplates,
  assessedPeriodNames,
  ...props
}) => {
  const { formatMessage } = useIntl()
  const { sorting, toggleSorting } = useSort()
  const [page, pageSize, setPage, setPageSize] = usePagination()
  const userFilters = useGetApiQueryFilters(allowedFilters)
  const [isUpdateDrawerOpen, openUpdateDrawer, closeUpdateDrawer] = useDrawer_DEPRECATED(false)
  const { openDrawer } = useDrawer()
  const hasWriteAccess = useWriteAccess()

  const basePayload = {
    filter: [
      {
        name: 'assessmentTemplateId',
        filters: [
          {
            operator: Operators.NotEqualTo,
            value: 0,
          },
        ],
      },
    ],
    sort: sorting,
    include: ['assessmentTemplate', 'provider.country.risks.riskType', 'provider.organization.contacts.user'],
  }

  const payload = {
    filter: [
      ...userFilters,
      {
        name: 'assessmentTemplateId',
        filters: [
          {
            operator: Operators.In,
            value: activatedAssessmentTemplates.map(x => x.id),
          },
        ],
      },
    ],
    sort: sorting,
    include: ['provider', 'assessmentTemplate', 'provider.providerOwners.user'],
  }

  const {
    items: assessments,
    isLoading,
    isFetching,
    count,
    isError,
  } = useFetchCollectionWithPost<Assessment>({
    key: FetchKey.AssessmentCollection,
    endpoint: endpoints.assessmentCollection,
    payload,
    options: { enabled: Boolean(activatedAssessmentTemplates?.length) },
  })

  const { uniqueProviders, uniqueSortedAssessmentTemplateAndPeriod, structuredData } =
    useOrganizedAssessmentData(assessments)

  const { selectedRowsIds, setSelectedRowsIds, handleCheckboxChange, isHeaderChecked, handleHeaderCheckboxChange } =
    useSelectableRows<Provider>({
      rowsInPage: uniqueProviders,
    })

  const selectedProvidersForExpectation = useMemo(() => {
    const providerMap = new Map<number, Provider & { assessments: Assessment[] }>()
    for (const assessment of assessments) {
      if (!selectedRowsIds.includes(assessment.provider.id)) continue
      const existing = providerMap.get(assessment.provider.id)
      if (existing) {
        existing.assessments.push(assessment)
      } else {
        providerMap.set(assessment.provider.id, { ...assessment.provider, assessments: [assessment] })
      }
    }
    return [...providerMap.values()] as Provider[]
  }, [assessments, selectedRowsIds])

  const {
    isEnabled: showBaselineExpectation,
    open: openBulkCreateExpectation,
    modal: bulkExpectationModal,
  } = useBulkCreateExpectation(selectedProvidersForExpectation)

  const handleExport = (selectedProviderIds?: number[]) => {
    openDrawer(
      <DrawerViewExport
        resourceType={ResourceTypes.Assessment}
        resourceView={AllAssessmentsViews.Compare}
        count={count}
        userFilter={userFilters}
        exportColumns={AllAssessmentsExportColumnsAccessor}
        rawExportPayload={basePayload}
        disableExportSettings
        selectedIds={selectedProviderIds}
        selectedFilterField="provider.id"
      />,
    )
  }

  return (
    <>
      <ResourceCollectionScene
        enableScroll
        title={formatMessage({ id: 'navbar.assessments' })}
        filter={
          <AssessmentFilters
            allowedFilters={allowedFilters}
            preSelectedAssessmentTemplateIds={activatedAssessmentTemplates.map(x => x.id)}
            preSelectedPeriodNames={assessedPeriodNames}
          />
        }
        actionButtons={[
          ...insertIf<ActionButton>(hasWriteAccess, {
            label: formatMessage({ id: 'assessments.setAssessments' }),
            variant: 'outlined',
            to: generatePath(paths.configurations, {
              configurationsPage: CONFIGURATION_PAGE_IDS.Assessments,
            }),
          }),
          {
            label: formatMessage({ id: 'resourceCollections.general.export' }),
            variant: 'outlined',
            onClick: () => handleExport(selectedRowsIds),
            disabled: isLoading || count === 0,
          },
        ]}
        buttonRow={
          selectedRowsIds.length
            ? [
                ...insertIf<ActionButton>(hasWriteAccess, {
                  label: formatMessage(
                    { id: 'resourceCollections.general.categorize' },
                    { count: selectedRowsIds.length },
                  ),
                  onClick: openUpdateDrawer,
                  disabled: isLoading || isFetching,
                }),
                ...insertIf<ActionButton>(hasWriteAccess, {
                  label: formatMessage({ id: 'assessments.setYourRiskAssessment' }, { count: selectedRowsIds.length }),
                  onClick: () =>
                    openDrawer(
                      <DrawerViewRiskAssessment
                        providerIds={selectedRowsIds}
                        pageOpenedFromName={AmplitudeTrackingPages.AssessmentComparePage}
                      />,
                    ),
                  disabled: isLoading || isFetching,
                }),
                ...insertIf<ActionButton>(hasWriteAccess && Boolean(showBaselineExpectation), {
                  label: formatMessage({ id: 'bulkCreateExpectation.entryButton' }, { count: selectedRowsIds.length }),
                  startIcon: <PublishedWithChangesOutlined />,
                  onClick: openBulkCreateExpectation,
                  disabled: isLoading || isFetching,
                }),
                {
                  label: formatMessage(
                    { id: 'resourceCollections.general.exportWithCount' },
                    { count: selectedRowsIds.length },
                  ),
                  onClick: () => handleExport(selectedRowsIds),
                  disabled: isLoading || isFetching,
                },
              ]
            : undefined
        }
        {...props}
      >
        <Table<Provider>
          RowComponent={({ row }): JSX.Element => (
            <AllAssessmentsCompareRow
              row={row}
              onCheckboxChange={handleCheckboxChange}
              selectedProviders={selectedRowsIds}
              providerAssessmentData={structuredData}
            />
          )}
          HeaderComponent={() => (
            <AllAssessmentsCompareHeader
              toggleSorting={toggleSorting}
              activeSorting={sorting}
              assessmentTemplateAndPeriod={uniqueSortedAssessmentTemplateAndPeriod}
              handleHeaderCheckboxChange={handleHeaderCheckboxChange}
              isHeaderChecked={isHeaderChecked}
            />
          )}
          data={uniqueProviders}
          count={count}
          isLoading={isLoading || isFetching}
          isError={isError}
          page={page}
          pageSize={pageSize}
          setPage={setPage}
          setPageSize={setPageSize}
          noPagination
          stickyColumnIndex={2}
          emptyState={
            <EmptyState
              title={formatMessage({ id: 'assessments.compare.emptyStateTitle' }, { br })}
              description={formatMessage({ id: 'assessments.compare.emptyStateDescription' })}
              iconComponent={ChatIcon}
            />
          }
        />
      </ResourceCollectionScene>

      <UpdateProvidersDrawer
        selectedProviders={uniqueProviders
          .filter(provider => selectedRowsIds.includes(provider.id))
          .map(provider => ({
            providerId: provider.id,
            name: provider.name,
            activityStatus: provider.activityStatus,
            finalRiskRating: provider.finalRiskRating,
            priority: provider.priority,
            providerApprovalStatus: provider.providerApprovalStatus,
            categoryOptionIds: provider.categoryOptions?.map(catOpt => catOpt.id),
            ownerUserIds: provider.ownerUserIds,
            tier: provider.tier,
            supplierUsage: provider.supplierUsage,
          }))}
        setSelectedProviders={setSelectedRowsIds}
        isDrawerOpen={isUpdateDrawerOpen}
        closeDrawer={closeUpdateDrawer}
        pageOpenedFromName={AmplitudeTrackingPages.AssessmentComparePage}
      />
      {bulkExpectationModal}
    </>
  )
}
export default AssessmentCompareScene
